import type {
    BimWorkOrderPriceData,
    BimWorkOrderTotalItemData,
    ContractStatusEnum,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { BimWorkOrderRowDetailsType } from 'pages/Project/BimProject/WorkOrder/types';

import type { CellClassParams, ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import type { IBimWorkOrderRowDetails } from '../cellRenderers';
import { BimWorkOrderMenuRenderer } from '../cellRenderers';

export const useWorkOrderMenuVersionColumn = ({
    makeRefreshTable,
    workOrderStatus,
    contractStatus,
    orderRules,
    statusProject,
}: {
    makeRefreshTable: () => void;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
}) => {
    const [checkedPriceRowForDetails, setCheckedPriceRowForDetails] = useState<IBimWorkOrderRowDetails<
        BimWorkOrderTotalItemData | BimWorkOrderPriceData | null
    > | null>(null);

    const [prevCheckedPriceRowForDetails, setPrevCheckedPriceRowForDetails] = useState<IBimWorkOrderRowDetails<
        BimWorkOrderTotalItemData | BimWorkOrderPriceData | null
    > | null>(null);

    const [checkedRows, setCheckedRows] = useState({});

    const isHistoryDetails = useMemo(
        () =>
            checkedPriceRowForDetails?.detailsType &&
            [BimWorkOrderRowDetailsType.Revision, BimWorkOrderRowDetailsType.Update].includes(
                checkedPriceRowForDetails?.detailsType,
            ),
        [checkedPriceRowForDetails?.detailsType],
    );

    const selectRowAndOpenDetailsModal = useCallback(
        (data: IBimWorkOrderRowDetails<BimWorkOrderTotalItemData | BimWorkOrderPriceData | null>) => {
            setCheckedPriceRowForDetails((prevState) => {
                if (prevState) {
                    setPrevCheckedPriceRowForDetails(prevState);
                }

                return data;
            });
        },
        [],
    );

    const closeDetailsModalAndCleanChecked = useCallback(() => {
        if (prevCheckedPriceRowForDetails) {
            setCheckedPriceRowForDetails(prevCheckedPriceRowForDetails);
            setPrevCheckedPriceRowForDetails(null);
        } else {
            setCheckedPriceRowForDetails(null);
        }
    }, [prevCheckedPriceRowForDetails]);

    const checkRowHandler = useCallback(({ id, data }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: data }));
    }, []);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const menuColumn = useMemo<ColDef<BimWorkOrderPriceData>>(
        () => ({
            headerName: '',
            field: 'menu' as any,
            width: 90,
            cellRendererSelector: (params) => {
                const { data, node } = params;

                if (data?.isLimitPrice) {
                    return undefined;
                }

                if (node.rowPinned === 'bottom') {
                    return {
                        component: BimWorkOrderMenuRenderer<true>({
                            selectRowAndOpenDetailsModal,
                            checkRowHandler,
                            checkedRows,
                            isBottomTotalRow: true,
                            makeRefreshTable,
                            hideCheckBoxAndMenu: isHistoryDetails,
                            workOrderStatus,
                            contractStatus,
                            orderRules,
                            statusProject,
                        }),
                    };
                }

                return {
                    component: BimWorkOrderMenuRenderer<false>({
                        selectRowAndOpenDetailsModal,
                        checkRowHandler,
                        checkedRows,
                        isBottomTotalRow: false,
                        makeRefreshTable,
                        hideCheckBoxAndMenu: isHistoryDetails,
                        workOrderStatus,
                        contractStatus,
                        orderRules,
                        statusProject,
                    }),
                };
            },
            cellClassRules: {
                bottomPinnedMenuCell: (params: CellClassParams) => params.node.rowPinned === 'bottom',
                centeredAgGridCell: (params: CellClassParams) => !params.node.rowPinned,
            },
            spanHeaderHeight: true,
            resizable: false,
            pinned: 'left',
            lockPosition: 'left',
        }),
        [
            selectRowAndOpenDetailsModal,
            checkRowHandler,
            checkedRows,
            makeRefreshTable,
            isHistoryDetails,
            workOrderStatus,
            contractStatus,
            orderRules,
            statusProject,
        ],
    );

    return { menuColumn, checkedPriceRowForDetails, closeDetailsModalAndCleanChecked, checkedRows, resetCheckedRows };
};
